/**
 * v3.30.12 · Cobertura de Pagador de Terceros (Curaduría Oficial).
 *
 * Se muestra en la solapa Facturación del residente. Permite:
 *  - Activar la cobertura eligiendo convenio pagador + conceptos + vigencia.
 *  - Ver el estado actual (activa/vencida/cerrada) y datos del pagador.
 *  - Abrir el wizard de baja: lista FCs impagas en CC Curaduría, deja al
 *    usuario emitir NC-ARCA vinculadas y transferir el saldo a la CC del
 *    residente + destildar el checkbox.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { HandHeart, ShieldOff, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ResidentThirdPartyPayer({ residentId, resident, services, canEdit, onChange }) {
  const [payers, setPayers] = useState([]);
  const [coverage, setCoverage] = useState(resident?.third_party_payer_coverage || null);
  const [showForm, setShowForm] = useState(false);
  const [showCloseWizard, setShowCloseWizard] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [closingBusy, setClosingBusy] = useState(false);
  const [form, setForm] = useState({
    payer_insurer_id: '', covered_service_ids: [],
    valid_from: new Date().toISOString().slice(0, 10), valid_to: '', notes: '',
  });

  useEffect(() => setCoverage(resident?.third_party_payer_coverage || null),
    [resident?.third_party_payer_coverage]);

  // v3.30.12 · Re-consultar la cobertura cada vez que se abre la ficha del
  // residente, así vemos siempre el estado real del backend (por ejemplo
  // después de que otro operador la cerró, o después del wizard de baja).
  useEffect(() => {
    if (!residentId) return;
    let alive = true;
    axios.get(`${BACKEND_URL}/api/residents/${residentId}/third-party-coverage`,
              { withCredentials: true })
      .then(r => { if (alive) setCoverage(r.data?.coverage || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [residentId]);

  const loadPayers = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/health-insurers/third-party-payers`,
        { withCredentials: true });
      setPayers(r.data || []);
    } catch (e) { setPayers([]); }
  }, []);
  useEffect(() => { loadPayers(); }, [loadPayers]);

  const payer = useMemo(() => payers.find(p => p.insurer_id === coverage?.payer_insurer_id),
    [payers, coverage]);
  const coveredServices = useMemo(() => {
    if (!coverage) return [];
    return services.filter(s => (coverage.covered_service_ids || []).includes(s.service_id));
  }, [coverage, services]);

  // Elegibles para cubrir: adicionales activos.
  const eligibleServices = useMemo(
    () => services.filter(s => s.service_class === 'adicional' && s.is_active !== false),
    [services]);

  const openNew = () => {
    setForm({
      payer_insurer_id: coverage?.payer_insurer_id || (payers[0]?.insurer_id || ''),
      covered_service_ids: coverage?.covered_service_ids || [],
      valid_from: coverage?.valid_from || new Date().toISOString().slice(0, 10),
      valid_to: coverage?.valid_to || '',
      notes: coverage?.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.payer_insurer_id) { toast.error('Elegí el convenio pagador'); return; }
    if (!form.covered_service_ids.length) { toast.error('Seleccioná al menos un concepto cubierto'); return; }
    if (!form.valid_from) { toast.error('Ingresá la vigencia Desde'); return; }
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/residents/${residentId}/third-party-coverage`,
        {
          payer_insurer_id: form.payer_insurer_id,
          covered_service_ids: form.covered_service_ids,
          valid_from: form.valid_from,
          valid_to: form.valid_to || null,
          notes: form.notes || null,
        }, { withCredentials: true });
      toast.success('Cobertura guardada');
      setCoverage(r.data?.coverage || null);
      setShowForm(false);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar cobertura'); }
  };

  const openCloseWizard = async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/residents/${residentId}/third-party-coverage/unpaid-invoices`,
        { withCredentials: true });
      setUnpaidInvoices(r.data?.invoices || []);
      setSelectedIds(new Set((r.data?.invoices || []).map(i => i.invoice_id)));
      setShowCloseWizard(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar facturas'); }
  };

  const confirmClose = async () => {
    setClosingBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/residents/${residentId}/third-party-coverage/close`,
        {
          invoice_ids: Array.from(selectedIds),
          valid_to: new Date().toISOString().slice(0, 10),
          nc_issue_date: new Date().toISOString().slice(0, 10),
          nc_reason: `Baja Curaduría · ${payer?.name || ''}`,
        }, { withCredentials: true });
      toast.success(`Baja completada · ${(r.data?.credit_notes || []).length} NC-ARCA emitidas`);
      // v3.30.12 · Reflejar el estado nuevo en el UI sin depender de que el
      // padre recargue la ficha completa. La cobertura viene con is_active=false.
      if (r.data?.coverage) setCoverage(r.data.coverage);
      else await reloadCoverage();
      setShowCloseWizard(false);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cerrar la cobertura'); }
    finally { setClosingBusy(false); }
  };

  const reloadCoverage = useCallback(async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/residents/${residentId}/third-party-coverage`,
        { withCredentials: true });
      setCoverage(r.data?.coverage || null);
    } catch (_) { /* no-op */ }
  }, [residentId]);

  const isActive = coverage?.is_active;
  const cutByStatus = coverage?.auto_terminated_by_status;

  return (
    <Card data-testid="resident-third-party-payer">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <HandHeart className="w-4 h-4 text-fuchsia-600" />
          <h3 className="text-sm font-semibold">E · Pagador de Terceros (Curaduría)</h3>
          <span className="text-[11px] text-muted-foreground">
            Cobertura selectiva: las FC-ARCA de los conceptos cubiertos van a la CC del pagador
          </span>
        </div>

        {!coverage && (
          <div className="rounded-md border bg-muted/40 px-3 py-3 text-xs space-y-2">
            <p className="text-muted-foreground">
              Este residente no tiene cobertura de Pagador de Terceros. Activala si la Curaduría
              (u otro convenio marcado como tal) financia determinados conceptos.
            </p>
            <Button size="sm" variant="outline" onClick={openNew} disabled={!canEdit}
                    data-testid="tpp-activate-btn">
              <HandHeart className="w-3.5 h-3.5 mr-1" />
              Activar cobertura
            </Button>
          </div>
        )}

        {coverage && (
          <div className="rounded-md border bg-fuchsia-50/40 dark:bg-fuchsia-950/20 px-3 py-2 text-xs space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}>
                {isActive ? 'ACTIVA' : 'CERRADA'}
              </Badge>
              <b>{payer?.name || coverage.payer_insurer_id}</b>
              <span className="text-muted-foreground">
                · vigencia {coverage.valid_from}{coverage.valid_to ? ` → ${coverage.valid_to}` : ' → sin fecha final'}
              </span>
              {cutByStatus && (
                <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700">
                  Cortada automáticamente por {cutByStatus.replace('_', ' ')}
                </Badge>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Conceptos cubiertos: </span>
              {coveredServices.length === 0
                ? <i className="text-muted-foreground">— ninguno —</i>
                : coveredServices.map(s => (
                    <Badge key={s.service_id} variant="outline" className="mr-1 text-[10px]"
                           data-testid={`tpp-covered-${s.service_id}`}>
                      {s.code ? `${s.code} · ` : ''}{s.name}
                    </Badge>
                  ))
              }
            </div>
            {cutByStatus && isActive === false && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/70 dark:bg-amber-950/30 px-2 py-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                <span className="text-amber-800 dark:text-amber-200 text-[11px]">
                  El residente pasó a estado terminal. Regularizá con NC-ARCA las FCs impagas
                  del pagador desde el wizard de baja.
                </span>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={openNew} disabled={!canEdit}
                      data-testid="tpp-edit-btn">
                Editar cobertura
              </Button>
              <Button size="sm" variant="outline"
                      onClick={openCloseWizard} disabled={!canEdit}
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      data-testid="tpp-close-btn">
                <ShieldOff className="w-3.5 h-3.5 mr-1" />
                Dar de baja (NC-ARCA)
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* ===== Diálogo alta/edición ===== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="tpp-form-dialog">
          <DialogHeader>
            <DialogTitle>Pagador de Terceros — {resident?.full_name}</DialogTitle>
            <DialogDescription>
              La FC-ARCA se emite con CUIT del residente pero se agrupa en la CC del convenio pagador.
              El comprobante fiscal no cambia. Se aplica solo a los conceptos seleccionados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Convenio pagador *</Label>
              <select value={form.payer_insurer_id}
                      onChange={(e) => setForm(f => ({ ...f, payer_insurer_id: e.target.value }))}
                      className="flex h-10 w-full rounded-md border px-2 text-sm"
                      data-testid="tpp-payer-select">
                <option value="">— Elegí un pagador —</option>
                {payers.map(p => (
                  <option key={p.insurer_id} value={p.insurer_id}>
                    {p.name} · CUIT {p.tax_id}
                  </option>
                ))}
              </select>
              {payers.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  No hay convenios marcados como Pagador de Terceros. Marcalos en Convenios.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vigencia desde *</Label>
                <Input type="date" value={form.valid_from}
                       onChange={(e) => setForm(f => ({ ...f, valid_from: e.target.value }))}
                       data-testid="tpp-valid-from" />
              </div>
              <div>
                <Label className="text-xs">Vigencia hasta</Label>
                <Input type="date" value={form.valid_to}
                       onChange={(e) => setForm(f => ({ ...f, valid_to: e.target.value }))}
                       data-testid="tpp-valid-to" />
              </div>
            </div>
            <div className="rounded-md border p-2 space-y-1 max-h-48 overflow-y-auto">
              <Label className="text-xs">Conceptos cubiertos *</Label>
              <p className="text-[11px] text-muted-foreground">
                Solo los conceptos aquí seleccionados generan FC-ARCA hacia la CC del pagador.
                El resto sigue el circuito habitual (Residente / Convenio).
              </p>
              {eligibleServices.length === 0 && (
                <p className="text-[11px] text-amber-700">
                  No hay conceptos adicionales activos en el catálogo.
                </p>
              )}
              {eligibleServices.map(s => {
                const on = (form.covered_service_ids || []).includes(s.service_id);
                return (
                  <label key={s.service_id}
                         className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <input type="checkbox" className="h-3.5 w-3.5" checked={on}
                           onChange={() => setForm(f => ({
                             ...f, covered_service_ids: on
                               ? f.covered_service_ids.filter(x => x !== s.service_id)
                               : [...f.covered_service_ids, s.service_id],
                           }))}
                           data-testid={`tpp-covered-toggle-${s.service_id}`} />
                    <span>{s.code ? `${s.code} · ` : ''}{s.name}</span>
                    <span className="text-muted-foreground">{fmtMoney(s.current_price)}</span>
                  </label>
                );
              })}
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Input value={form.notes}
                     onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                     placeholder="Ej: expediente Curaduría N° 1234" data-testid="tpp-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!canEdit || !form.payer_insurer_id}
                    data-testid="tpp-save-btn">
              Guardar cobertura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Wizard baja ===== */}
      <Dialog open={showCloseWizard} onOpenChange={setShowCloseWizard}>
        <DialogContent className="max-w-3xl" data-testid="tpp-close-wizard">
          <DialogHeader>
            <DialogTitle>Baja de cobertura — {resident?.full_name}</DialogTitle>
            <DialogDescription>
              Se emitirá una NC-ARCA por cada FC seleccionada. El saldo neto pasa a la CC del residente.
              El comprobante fiscal (NC-ARCA) se emite con CUIT del residente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {unpaidInvoices.length === 0 ? (
              <div className="text-sm text-muted-foreground rounded-md border p-3">
                No hay FCs impagas en la CC de {payer?.name || 'la Curaduría'}. Podés cerrar la
                cobertura sin emitir NCs (destilda el checkbox y guarda).
              </div>
            ) : (
              <>
                <div className="rounded-md border bg-amber-50/70 dark:bg-amber-950/20 p-2 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    Cada NC-ARCA se emite con CUIT del residente y vincula la FC de origen.
                    La FC deja la CC de {payer?.name} y se traslada a la CC del residente.
                    La NC neteará la deuda, quedando en su ficha el saldo neto (si lo hubiera).
                  </p>
                </div>
                <div className="max-h-72 overflow-y-auto border rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/60">
                      <tr>
                        <th className="p-2 text-left w-8">
                          <input type="checkbox"
                                 checked={selectedIds.size === unpaidInvoices.length}
                                 onChange={(e) => setSelectedIds(
                                   e.target.checked
                                     ? new Set(unpaidInvoices.map(i => i.invoice_id))
                                     : new Set())}
                                 data-testid="tpp-select-all" />
                        </th>
                        <th className="p-2 text-left">Comprobante</th>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-right">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaidInvoices.map(inv => {
                        const on = selectedIds.has(inv.invoice_id);
                        return (
                          <tr key={inv.invoice_id} className="border-t">
                            <td className="p-2">
                              <input type="checkbox" checked={on}
                                     onChange={() => setSelectedIds(prev => {
                                       const n = new Set(prev);
                                       if (on) n.delete(inv.invoice_id); else n.add(inv.invoice_id);
                                       return n;
                                     })}
                                     data-testid={`tpp-inv-check-${inv.invoice_id}`} />
                            </td>
                            <td className="p-2">{inv.invoice_number}</td>
                            <td className="p-2">{inv.issue_date}</td>
                            <td className="p-2 text-right">{fmtMoney(inv.total)}</td>
                            <td className="p-2 text-right font-medium">{fmtMoney(inv.outstanding)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseWizard(false)}>Cancelar</Button>
            <Button className="bg-rose-700 hover:bg-rose-800 text-white"
                    onClick={confirmClose}
                    disabled={closingBusy}
                    data-testid="tpp-close-confirm">
              {closingBusy ? 'Procesando…' :
                (unpaidInvoices.length === 0
                  ? 'Cerrar cobertura'
                  : `Emitir ${selectedIds.size} NC-ARCA y cerrar cobertura`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ResidentThirdPartyPayer;
